import Header from "../components/Header";
import Footer from "../components/Footer";
import PageSeo from "../components/PageSeo";
import { SUPPORT_EMAIL, absoluteUrl } from "../seo/siteData";

function TermsConditions() {
  const currentYear = new Date().getFullYear();
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms and Conditions - Zarkit",
      url: absoluteUrl("/terms-conditions"),
      description:
        "Read the Zarkit terms and conditions for local delivery, goods transport, and account policies.",
    },
  ];

  return (
    <>
      <PageSeo
        title="Terms and Conditions"
        description="Read the Zarkit terms and conditions covering local delivery, goods transport bookings, driver-partner responsibilities, and account policies."
        path="/terms-conditions"
        keywords="Zarkit terms, Jhansi delivery terms, goods transport terms Zarkit"
        structuredData={structuredData}
      />

      <div className="site-shell">
        <Header />

        <main>
          <div className="terms-wrap">

            {/* ===== TERMS & CONDITIONS ===== */}
            <div className="doc-title">Terms &amp; Conditions</div>
            <div className="meta">Effective Date: 02-03-2026 &nbsp;|&nbsp; Last Updated: 02-03-2026</div>

            <div className="intro-block">
              <p>These Terms &amp; Conditions ("Terms") govern your access to and use of the Zarkit mobile application, website, and logistics services (collectively, the "Services").</p>
              <p>By accessing, registering, or using Zarkit, you agree to be bound by these Terms. If you do not agree, please do not use the Services.</p>
            </div>

            {/* Section 1 */}
            <h2><span className="sec-badge">1</span> Company Information</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
                <li><strong>Owner:</strong> Ravi Kumar</li>
                <li>
                  <strong>Registered Address:</strong><br />
                  Jouri Bujurg, Near Parichha,<br />
                  Jhansi, Uttar Pradesh – 284121, India
                </li>
                <li><strong>Support Email:</strong> {SUPPORT_EMAIL}</li>
              </ul>
            </div>

            {/* Section 2 */}
            <h2><span className="sec-badge">2</span> Nature of Services</h2>
            <p>Zarkit is an on-demand logistics and delivery platform that connects:</p>
            <ul>
              <li>Customers needing goods transportation</li>
              <li>Independent delivery partners / drivers</li>
            </ul>
            <p>Zarkit does not own vehicles and does not directly provide transportation services.</p>
            <p>Zarkit acts only as a technology platform and facilitator.</p>

            {/* Section 3 */}
            <h2><span className="sec-badge">3</span> Eligibility to Use Services</h2>
            <p>To use Zarkit, you must:</p>
            <ul>
              <li>Be at least 18 years old</li>
              <li>Provide accurate and complete information</li>
              <li>Be legally capable of entering into a binding contract</li>
            </ul>
            <p>Zarkit reserves the right to suspend or terminate accounts that violate these conditions.</p>

            {/* Section 4 */}
            <h2><span className="sec-badge">4</span> User Accounts</h2>
            <h3>4.1 Registration</h3>
            <p>To access certain features, you must create an account using a valid mobile number or email.</p>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining account confidentiality</li>
              <li>All activities conducted through your account</li>
            </ul>
            <h3>4.2 Account Suspension</h3>
            <p>Zarkit may suspend or terminate your account if:</p>
            <ul>
              <li>False or misleading information is provided</li>
              <li>Fraudulent or illegal activity is detected</li>
              <li>These Terms are violated</li>
            </ul>

            {/* Section 5 */}
            <h2><span className="sec-badge">5</span> Booking &amp; Delivery Terms</h2>
            <h3>5.1 Customer Responsibilities</h3>
            <p>Customers must:</p>
            <ul>
              <li>Provide accurate pickup and drop details</li>
              <li>Ensure goods are properly packed</li>
              <li>Ensure goods are legal, safe, and allowed for transport</li>
              <li>Be available at pickup and delivery time</li>
            </ul>
            <h3>5.2 Delivery Partner Assignment</h3>
            <p>Delivery partners are assigned based on:</p>
            <ul>
              <li>Location</li>
              <li>Availability</li>
              <li>Vehicle suitability</li>
            </ul>
            <p>Zarkit does not guarantee delivery partner availability at all times.</p>

            {/* Section 6 */}
            <h2><span className="sec-badge">6</span> Prohibited Items</h2>
            <p>The following items are strictly prohibited:</p>
            <div className="warn-panel">
              <ul>
                <li>Illegal goods</li>
                <li>Explosives, weapons, firearms</li>
                <li>Narcotics or controlled substances</li>
                <li>Hazardous, flammable, or toxic materials</li>
                <li>Cash, jewellery, precious metals (unless explicitly allowed)</li>
                <li>Live animals</li>
                <li>Perishable items without proper packaging</li>
              </ul>
            </div>
            <p>Zarkit reserves the right to cancel orders involving prohibited items.</p>

            {/* Section 7 */}
            <h2><span className="sec-badge">7</span> Pricing &amp; Payments</h2>
            <h3>7.1 Pricing</h3>
            <p>Delivery charges are:</p>
            <ul>
              <li>Displayed before order confirmation</li>
              <li>Based on distance, time, weight, and demand</li>
            </ul>
            <p>Prices may change due to traffic, delays, or route changes.</p>
            <h3>7.2 Payments</h3>
            <p>Payments may be made via:</p>
            <ul>
              <li>UPI</li>
              <li>Cards</li>
              <li>Wallets</li>
              <li>Other supported payment methods</li>
            </ul>
            <p>Zarkit uses third-party payment gateways and does not store sensitive payment data.</p>

            {/* Section 8 */}
            <h2><span className="sec-badge">8</span> Cancellation &amp; Refund Policy</h2>
            <h3>8.1 Customer Cancellation</h3>
            <ul>
              <li>Cancellation before pickup may incur minimal charges</li>
              <li>Cancellation after pickup may result in no refund</li>
            </ul>
            <h3>8.2 Zarkit Cancellation</h3>
            <p>Zarkit may cancel orders due to:</p>
            <ul>
              <li>Unavailability of delivery partners</li>
              <li>Safety concerns</li>
              <li>Violation of Terms</li>
            </ul>
            <p>Refunds, if applicable, will be processed within 5–10 working days.</p>

            {/* Section 9 */}
            <h2><span className="sec-badge">9</span> Delays &amp; Force Majeure</h2>
            <p>Zarkit is not responsible for delays caused by:</p>
            <ul>
              <li>Traffic conditions</li>
              <li>Weather</li>
              <li>Natural disasters</li>
              <li>Government restrictions</li>
              <li>Strikes or unforeseen events</li>
            </ul>

            {/* Section 10 */}
            <h2><span className="sec-badge">10</span> Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li>Zarkit is not liable for indirect, incidental, or consequential damages</li>
              <li>Zarkit is not responsible for loss or damage due to improper packaging</li>
              <li>Liability, if any, shall not exceed the delivery fee paid</li>
            </ul>

            {/* Section 11 */}
            <h2><span className="sec-badge">11</span> Goods Responsibility</h2>
            <p>Customers are solely responsible for:</p>
            <ul>
              <li>Declaring correct item details</li>
              <li>Packaging and safety of goods</li>
            </ul>
            <p>Zarkit does not insure goods unless explicitly stated.</p>

            {/* Section 12 */}
            <h2><span className="sec-badge">12</span> Delivery Partner Terms (Brief)</h2>
            <p>Delivery partners:</p>
            <ul>
              <li>Are independent contractors, not employees</li>
              <li>Are responsible for valid licenses, vehicle condition, and legal compliance</li>
              <li>Must follow Zarkit service standards</li>
            </ul>
            <p>Zarkit may deactivate partners for misconduct or policy violations.</p>

            {/* Section 13 */}
            <h2><span className="sec-badge">13</span> Intellectual Property</h2>
            <p>All content including:</p>
            <ul>
              <li>Logo</li>
              <li>App design</li>
              <li>Text</li>
              <li>Graphics</li>
              <li>Trademarks</li>
            </ul>
            <p>are the exclusive property of Zarkit Group.</p>
            <p>Unauthorized use is strictly prohibited.</p>

            {/* Section 14 */}
            <h2><span className="sec-badge">14</span> User Conduct</h2>
            <p>Users agree not to:</p>
            <ul>
              <li>Misuse the platform</li>
              <li>Engage in abusive or illegal behavior</li>
              <li>Attempt to hack or damage the app</li>
              <li>Harass delivery partners or staff</li>
            </ul>
            <p>Violation may lead to permanent account termination.</p>

            {/* Section 15 */}
            <h2><span className="sec-badge">15</span> Privacy Policy</h2>
            <p>Use of Zarkit is also governed by our Privacy Policy, which explains how user data is collected and used.</p>

            {/* Section 16 */}
            <h2><span className="sec-badge">16</span> Account Deletion</h2>
            <p>Users may request account deletion by contacting:</p>
            <p>✉ support@zarkit.com</p>
            <p>Deletion requests may take 7–30 working days.</p>

            {/* Section 17 */}
            <h2><span className="sec-badge">17</span> Modifications to Terms</h2>
            <p>Zarkit reserves the right to update these Terms at any time.</p>
            <p>Continued use after updates constitutes acceptance.</p>

            {/* Section 18 */}
            <h2><span className="sec-badge">18</span> Governing Law &amp; Jurisdiction</h2>
            <p>These Terms are governed by the laws of India.</p>
            <p>Courts in Uttar Pradesh shall have exclusive jurisdiction.</p>

            {/* Section 19 */}
            <h2><span className="sec-badge">19</span> Contact Information</h2>
            <div className="dark-contact">
              <p>For any questions or complaints:</p>
              <br />
              <p>✉ Email: <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
              <br />
              <p>
                🏠 Address:<br />
                Jouri Bujurg, Near Parichha,<br />
                Jhansi, Uttar Pradesh – 284121, India
              </p>
            </div>

            <hr className="section-divider" />

            {/* ===== ACCOUNT DELETION POLICY ===== */}
            <div className="doc-subtitle">Account Deletion Policy – Zarkit</div>
            <div className="meta">Effective Date: [Add Date] &nbsp;|&nbsp; Last Updated: [Add Date]</div>

            <div className="intro-block">
              <p>Zarkit ("Zarkit", "we", "our", "us") respects your right to control your personal data.</p>
              <p>This Account Deletion Policy explains how users can request deletion of their Zarkit account and associated data.</p>
            </div>

            <h2><span className="sec-badge">1</span> Who Can Request Account Deletion</h2>
            <p>The following users may request account deletion:</p>
            <ul>
              <li>Customers / Users</li>
              <li>Delivery Partners / Drivers</li>
              <li>Business Account Users</li>
            </ul>
            <p>The request must be made using the registered mobile number or email ID linked to the Zarkit account.</p>

            <h2><span className="sec-badge">2</span> How to Request Account Deletion</h2>
            <p>You can request account deletion using the following methods:</p>
            <h3>Email Request</h3>
            <p>Send an email to: ✉ support@zarkit.com</p>
            <p><strong>Email Subject:</strong> Account Deletion Request – Zarkit</p>
            <p><strong>Email Must Include:</strong></p>
            <ul>
              <li>Registered mobile number</li>
              <li>Registered email address</li>
              <li>User type (Customer / Driver / Business)</li>
              <li>Reason for deletion (optional)</li>
            </ul>

            <h2><span className="sec-badge">3</span> Verification Process</h2>
            <p>To protect your account, Zarkit may verify your identity using:</p>
            <ul>
              <li>OTP on registered mobile number</li>
              <li>Confirmation email</li>
              <li>Additional details (if required)</li>
            </ul>
            <p>Only verified requests will be processed.</p>

            <h2><span className="sec-badge">4</span> Processing Time</h2>
            <ul>
              <li>Account deletion requests are processed within 7 to 30 working days</li>
              <li>
                The timeline may vary due to:
                <ul>
                  <li>Pending orders</li>
                  <li>Payment settlements</li>
                  <li>Legal or regulatory requirements</li>
                </ul>
              </li>
            </ul>

            <h2><span className="sec-badge">5</span> What Data Is Deleted</h2>
            <p>Upon successful deletion:</p>
            <ul>
              <li>Personal profile information</li>
              <li>Contact details</li>
              <li>Saved addresses</li>
              <li>App usage data</li>
            </ul>
            <p>This data will be permanently deleted or anonymized.</p>

            <h2><span className="sec-badge">6</span> Data That May Be Retained</h2>
            <p>As per Indian laws and regulatory requirements, Zarkit may retain certain data even after account deletion, including:</p>
            <ul>
              <li>Transaction records</li>
              <li>Invoice and payment history</li>
              <li>Legal, tax, or compliance-related data</li>
              <li>Fraud prevention and dispute records</li>
            </ul>
            <p>Such data is retained only for the legally required period and is not used for marketing.</p>

            <h2><span className="sec-badge">7</span> Effects of Account Deletion</h2>
            <p>Once your account is deleted:</p>
            <ul>
              <li>You will not be able to log in</li>
              <li>Order history will no longer be accessible</li>
              <li>Wallet balances (if any) may be forfeited</li>
              <li>You must create a new account to use Zarkit again</li>
            </ul>
            <div className="warn-panel">
              <p>⚠ Account deletion is permanent and irreversible.</p>
            </div>

            <h2><span className="sec-badge">8</span> Pending Orders &amp; Payments</h2>
            <ul>
              <li>
                Account deletion requests will not be processed if:
                <ul>
                  <li>There are active or ongoing deliveries</li>
                  <li>Payments or refunds are pending</li>
                </ul>
              </li>
            </ul>
            <p>Such requests will be processed only after resolution.</p>

            <h2><span className="sec-badge">9</span> Rejection of Request</h2>
            <p>Zarkit reserves the right to reject or delay deletion requests if:</p>
            <ul>
              <li>The request cannot be verified</li>
              <li>Legal obligations require data retention</li>
              <li>Fraud or misuse is under investigation</li>
            </ul>

            <h2><span className="sec-badge">10</span> Changes to This Policy</h2>
            <p>Zarkit may update this Account Deletion Policy from time to time.</p>
            <p>Any updates will be posted on this page with a revised date.</p>

            <h2><span className="sec-badge">11</span> Contact Information</h2>
            <div className="dark-contact">
              <p>For account deletion or related questions, contact:</p>
              <br />
              <p>✉ Email: <a href="mailto:support@zarkit.com">support@zarkit.com</a></p>
              <br />
              <p>
                🏠 Registered Address:<br />
                Jouri Bujurg, Near Parichha,<br />
                Jhansi, Uttar Pradesh – 284121, India
              </p>
            </div>

            <hr className="section-divider" />

            {/* ===== CONTACT US ===== */}
            <div className="doc-subtitle contact-green">Contact Us – Zarkit</div>
            <div className="meta">We're here to help you.</div>

            <div className="intro-block">
              <p>If you have any questions, concerns, feedback, or need support related to Zarkit services, please feel free to contact us using the details below.</p>
            </div>

            <h2>Get in Touch</h2>
            <h3>✉ Email Support</h3>
            <p>For all queries, support requests, complaints, or account-related issues:</p>
            <div className="info-panel">
              <p><strong>support@zarkit.com</strong></p>
              <p>We aim to respond within 24–48 working hours.</p>
            </div>

            <h2>Company Information</h2>
            <div className="info-panel">
              <ul>
                <li><strong>Brand Name:</strong> Zarkit</li>
                <li><strong>Registered Name:</strong> Zarkit Group</li>
                <li><strong>Owner:</strong> Ravi Kumar</li>
              </ul>
            </div>

            <h3>🏠 Registered Office Address</h3>
            <div className="info-panel">
              <p>
                Jouri Bujurg, Near Parichha,<br />
                Jhansi, Uttar Pradesh – 284121<br />
                India
              </p>
            </div>

            <h2>Support For</h2>
            <p>You may contact us for:</p>
            <ul>
              <li>Booking &amp; delivery support</li>
              <li>Payment or refund issues</li>
              <li>Account-related queries</li>
              <li>Account deletion requests</li>
              <li>Driver partner support</li>
              <li>Business or partnership inquiries</li>
              <li>Legal or compliance matters</li>
            </ul>

            <h2>Working Hours</h2>
            <div className="hours-grid">
              <div className="hours-card">
                <strong>Monday to Saturday</strong>
                9:00 AM – 6:00 PM
              </div>
              <div className="hours-card">
                <strong>Sunday &amp; Public Holidays</strong>
                Closed (Email support available)
              </div>
            </div>

            <h2>Important Note</h2>
            <div className="warn-panel">
              <p>Please ensure that you contact us using the <strong>registered mobile number or email ID</strong> associated with your Zarkit account for faster resolution.</p>
            </div>

            <h2>Updates</h2>
            <p>Zarkit may update contact details or support methods from time to time.</p>
            <p>Any changes will be reflected on this page.</p>

          </div>
        </main>

        <Footer currentYear={currentYear} />
      </div>
    </>
  );
}

export default TermsConditions;
